/**
 * ReviewSourceChip — inline provenance affordance for change-set review flows
 * (Modification Center, Award Comparison) where the source quote and
 * confidence already ride on the discrepancy payload itself
 * (source_after_quote / source_after_confidence / …), not in a persisted
 * SourceReference looked up by target object.
 *
 * SourceChip's useSourceRefsForTarget fetch doesn't apply here — there's no
 * ref to look up, only the quote/confidence copied onto this specific change
 * item — so this is pure props: no hooks, no query, no masking guard (a
 * change item isn't a masked field). Same trigger/popover idiom as
 * SourceChip so the two read as one visual language across Delivery.
 *
 * Renders nothing when there is neither a quote nor a positive confidence,
 * so surfaces without review-time provenance (most award-import items today
 * — award change items aren't yet linked to source refs server-side) render
 * unchanged.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Popover, PopoverContent, PopoverTrigger } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { FileSearch } from 'lucide-react';
import { useState } from 'react';
import { DocumentViewerDrawer } from '../../../../components/ProposalDashboard/DocumentViewer';
import { readableSnippet } from '../lib/readableSnippet';
import { parseSpan } from '../lib/sourceSpan';
export function ReviewSourceChip({ quote, confidence, page, label, projectId, artifactId, span }) {
    const [viewerOpen, setViewerOpen] = useState(false);
    const hasConfidence = typeof confidence === 'number' && confidence > 0;
    if (!quote && !hasConfidence) return null;
    const confidencePct = hasConfidence ? `${Math.round(confidence * 100)}%` : null;
    const parsedSpan = parseSpan(span);
    const openPage = page && page > 0 ? page : parsedSpan.page ?? 1;
    return /*#__PURE__*/ _jsxs(Popover, {
        children: [
            /*#__PURE__*/ _jsx(PopoverTrigger, {
                asChild: true,
                children: /*#__PURE__*/ _jsx("button", {
                    type: "button",
                    className: "ml-1 inline-flex items-center align-middle text-muted-foreground hover:text-foreground",
                    "aria-label": `Source for ${label}`,
                    title: "View source",
                    "data-testid": Testids.deliveryShared.reviewSourceChip,
                    children: /*#__PURE__*/ _jsx(FileSearch, {
                        className: "h-3 w-3",
                        "aria-hidden": true
                    })
                })
            }),
            /*#__PURE__*/ _jsxs(PopoverContent, {
                className: "w-80 text-sm",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mb-2 flex items-center justify-between",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium",
                                children: "Source"
                            }),
                            confidencePct && /*#__PURE__*/ _jsxs("span", {
                                className: "text-xs text-muted-foreground",
                                "data-testid": Testids.deliveryShared.reviewSourceChipConfidence,
                                children: [
                                    "confidence ",
                                    confidencePct
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("dl", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx(Row, {
                                k: "Document",
                                v: label
                            }),
                            !!page && page > 0 && /*#__PURE__*/ _jsx(Row, {
                                k: "Page",
                                v: `p.${page}`
                            })
                        ]
                    }),
                    quote && /*#__PURE__*/ _jsxs("blockquote", {
                        className: "mt-2 whitespace-pre-line border-l-2 border-muted pl-2 text-xs italic text-muted-foreground",
                        "data-testid": Testids.deliveryShared.reviewSourceChipQuote,
                        children: [
                            "“",
                            readableSnippet(quote),
                            "”"
                        ]
                    }),
                    projectId && artifactId && /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        className: "mt-2 w-full rounded border px-2 py-1 text-xs font-medium text-primary hover:bg-muted",
                        onClick: ()=>setViewerOpen(true),
                        "data-testid": Testids.deliveryShared.reviewSourceChipOpen,
                        children: [
                            "Open in document",
                            openPage > 1 || !!page && page > 0 ? ` (p.${openPage})` : ''
                        ]
                    })
                ]
            }),
            projectId && artifactId && viewerOpen && /*#__PURE__*/ _jsx("span", {
                onClick: (e)=>e.stopPropagation(),
                children: /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
                    isOpen: true,
                    onClose: ()=>setViewerOpen(false),
                    projectId: projectId,
                    document: {
                        documentId: artifactId,
                        filename: label
                    },
                    initialPage: openPage,
                    highlightBlockId: parsedSpan.blockId
                })
            })
        ]
    });
}
function Row({ k, v }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex justify-between gap-3",
        children: [
            /*#__PURE__*/ _jsx("dt", {
                className: "text-muted-foreground",
                children: k
            }),
            /*#__PURE__*/ _jsx("dd", {
                className: "truncate text-right",
                children: v
            })
        ]
    });
}
